import type { Outputs } from "@postdaily/api/routers/index";
import { Badge } from "@postdaily/ui/components/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@postdaily/ui/components/card";
import { Skeleton } from "@postdaily/ui/components/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@postdaily/ui/components/table";
import { cn } from "@postdaily/ui/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";

import { portalTrpcClient } from "@/lib/portal-trpc";
import { createNoIndexHead } from "@/lib/seo";

/**
 * A creator's own numbers, behind the capability token in the URL.
 *
 * Same arrangement as the client review portal: no session, no app shell, no
 * analytics, and the token travels to the API in a POST body so it never lands
 * in a request log. Everything rendered here comes from an allowlisted payload
 * — the page cannot show a figure the API did not deliberately include.
 */
export const Route = createFileRoute("/(portal)/creator/$token")({
  head: () => createNoIndexHead("Creator stats · PostDaily"),
  component: RouteComponent,
});

type CreatorStats = Outputs["creatorPortal"]["resolve"];
type PayoutMethod = CreatorStats["payouts"][number]["method"];

const creatorStatsKey = (token: string) =>
  ["portal", "creator", token] as const;

const usd = new Intl.NumberFormat("en-US", {
  currency: "USD",
  style: "currency",
});

function formatCents(cents: number) {
  return usd.format(cents / 100);
}

function formatRate(bps: number | null) {
  if (bps === null) {
    return "—";
  }

  return `${(bps / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;
}

/**
 * A conversion rate with no clicks behind it is unanswerable, not zero.
 * Rendering "0%" there would tell a creator their link converts badly when
 * nobody has clicked it yet.
 */
function formatConversionRate(rate: number | null) {
  if (rate === null) {
    return "—";
  }

  return `${(rate * 100).toLocaleString("en-US", { maximumFractionDigits: 1 })}%`;
}

const METHOD_LABELS: Record<PayoutMethod, string> = {
  bank_transfer: "Bank transfer",
  other: "Other",
  paypal: "PayPal",
  wise: "Wise",
};

function Stat({
  hint,
  label,
  tone = "neutral",
  value,
}: {
  hint?: string;
  label: string;
  tone?: "negative" | "neutral" | "positive";
  value: string;
}) {
  return (
    <Card className="gap-1 py-4">
      <CardHeader className="px-4">
        <CardDescription>{label}</CardDescription>
      </CardHeader>
      <CardContent className="px-4">
        <p
          className={cn(
            "font-semibold text-2xl tabular-nums",
            tone === "negative" && "text-destructive",
            tone === "positive" && "text-emerald-600 dark:text-emerald-400",
          )}
        >
          {value}
        </p>
        {hint ? (
          <p className="mt-1 text-muted-foreground text-xs">{hint}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function BalanceStat({ stats }: { stats: CreatorStats }) {
  if (stats.balanceDue < 0) {
    // The hint is cause-neutral on purpose. A negative balance is usually a
    // refund that landed after we paid on it, but it is also what an advance
    // or a corrected entry looks like — naming one cause would state as fact
    // something the figure alone does not know, in a creator's own earnings.
    return (
      <Stat
        hint="You have been paid more than has been earned so far. This is netted off your next payout."
        label="Carried forward"
        tone="negative"
        value={formatCents(stats.balanceDue)}
      />
    );
  }

  const belowMinimum = stats.balanceDue < stats.terms.minimumPayoutCents;

  return (
    <Stat
      hint={
        belowMinimum
          ? `Paid once your balance reaches ${formatCents(stats.terms.minimumPayoutCents)}.`
          : `Due in the next ${stats.terms.payoutCadence} payout.`
      }
      label="Balance due"
      tone={stats.balanceDue > 0 ? "positive" : "neutral"}
      value={formatCents(stats.balanceDue)}
    />
  );
}

function CodesCard({ stats }: { stats: CreatorStats }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Your codes</CardTitle>
        <CardDescription>
          Each code is one campaign. Clicks count visits to your{" "}
          <span className="font-mono">postdaily.app/r/CODE</span> link; sign-ups
          are customers who used your code or link; paying is how many of them
          have made a real payment.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {stats.codes.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No codes yet. We will send you one when your first campaign is set
            up.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead className="text-right">Clicks (30d)</TableHead>
                  <TableHead className="text-right">Clicks (all)</TableHead>
                  <TableHead className="text-right">Sign-ups</TableHead>
                  <TableHead className="text-right">Paying</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.codes.map((code) => (
                  <TableRow key={code.code}>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium font-mono">
                          {code.code}
                        </span>
                        {code.label ? (
                          <span className="text-muted-foreground text-xs">
                            {code.label}
                          </span>
                        ) : null}
                        {code.active ? null : (
                          <Badge variant="outline">Ended</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {code.clicks30d}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {code.clicksTotal}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {code.signups}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {code.paying}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PayoutsCard({ stats }: { stats: CreatorStats }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Payouts</CardTitle>
        <CardDescription>
          Every payment we have sent you, with the provider's reference.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {stats.payouts.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Nothing paid out yet. Payouts go out {stats.terms.payoutCadence}{" "}
            once your balance reaches{" "}
            {formatCents(stats.terms.minimumPayoutCents)}.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.payouts.map((payout) => (
                  <TableRow
                    key={`${payout.paidAt.toISOString()}-${payout.reference ?? ""}`}
                  >
                    <TableCell>
                      {dayjs(payout.paidAt).format("D MMM YYYY")}
                    </TableCell>
                    <TableCell>{METHOD_LABELS[payout.method]}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {payout.reference ?? "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCents(payout.amountCents)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RouteComponent() {
  const { token } = Route.useParams();

  const statsQuery = useQuery({
    queryFn: () => portalTrpcClient.creatorPortal.resolve.query({ token }),
    queryKey: creatorStatsKey(token),
    retry: false,
  });

  if (statsQuery.isPending) {
    return (
      <main className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-4 sm:p-8">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </main>
    );
  }

  if (statsQuery.isError) {
    return (
      <main className="mx-auto flex w-full max-w-3xl flex-col gap-3 p-4 sm:p-8">
        <h1 className="font-semibold text-xl">This link isn’t available</h1>
        <p className="text-muted-foreground text-sm">
          It may have been revoked or replaced. Ask your PostDaily contact for a
          fresh stats link.
        </p>
      </main>
    );
  }

  const stats = statsQuery.data;
  const { terms } = stats;

  return (
    <main className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-4 sm:p-8">
      <header className="flex flex-col gap-1.5">
        <p className="font-medium text-muted-foreground text-sm">
          PostDaily creator program
        </p>
        <h1 className="font-semibold text-2xl">{stats.partnerName}</h1>
        <p className="text-muted-foreground text-sm">
          {formatRate(stats.commissionBps)} recurring commission ·{" "}
          {terms.payoutHoldDays}-day refund hold ·{" "}
          {formatCents(terms.minimumPayoutCents)} minimum · paid{" "}
          {terms.payoutCadence}
        </p>
      </header>

      <section
        aria-label="Funnel"
        className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
      >
        <Stat
          hint={`${stats.clicksTotal} all time`}
          label="Link clicks (30d)"
          value={String(stats.clicks30d)}
        />
        <Stat
          hint="Customers who used your code or link"
          label="Sign-ups"
          value={String(stats.customersAttributed)}
        />
        <Stat
          hint="On a trial or free plan — nothing earned yet"
          label="Not yet paying"
          value={String(stats.signupsNotYetPaying)}
        />
        <Stat
          hint="Sign-ups who have made a real payment"
          label="Paying customers"
          value={String(stats.payingCustomers)}
        />
        <Stat
          hint={
            stats.conversionRate === null
              ? "Needs at least one link click"
              : "Paying customers per link click, all time"
          }
          label="Conversion rate"
          value={formatConversionRate(stats.conversionRate)}
        />
        <Stat
          hint="Net of discounts and refunds, before tax"
          label="Revenue generated"
          value={formatCents(stats.revenueGenerated)}
        />
      </section>

      <section
        aria-label="Earnings"
        className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
      >
        <Stat
          hint={`${formatRate(stats.commissionBps)} of revenue generated`}
          label="Commission earned"
          value={formatCents(stats.commissionEarned)}
        />
        <Stat
          hint={`Payments younger than ${terms.payoutHoldDays} days`}
          label="On hold"
          value={formatCents(stats.commissionOnHold)}
        />
        <Stat
          hint={`Total sent to you so far: ${formatCents(stats.paidOut)}`}
          label="Payable"
          value={formatCents(stats.commissionPayable)}
        />
        <BalanceStat stats={stats} />
      </section>

      <CodesCard stats={stats} />
      <PayoutsCard stats={stats} />

      <footer className="text-muted-foreground text-xs leading-relaxed">
        Commission is {formatRate(stats.commissionBps)} of the net revenue your
        referred customers pay us — after any discount, before tax, and net of
        refunds — for as long as they stay subscribed. A payment becomes payable{" "}
        {terms.payoutHoldDays} days after it clears, and balances of{" "}
        {formatCents(terms.minimumPayoutCents)} or more are paid{" "}
        {terms.payoutCadence} by PayPal or Wise. Figures update as payments
        arrive.
      </footer>
    </main>
  );
}
